import { isEmpty } from 'underscore';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Panel } from 'react-bootstrap';
import { FontIcon } from '@fluentui/react';
import { Alert } from '@bingads-webui-react/alert';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';

import { AgeRanges, formatAgeRange } from '../constants/age-range';
import { Genders, formatGender } from '../constants/gender';
import { ProfileType } from '../constants/profile';
import { Demographic } from './demographic';
import { DemographicPicker } from './demographic-picker';
import { getDemographicsSectionStyle } from '../styles/demographics-section';

const UnstyledDemographicsSection = (props) => {
  const {
    classes,
    i18n,
    ageRanges,
    genders,
    companyName,
    industry,
    jobFunction,
  } = props;

  const serverContext = useContext(ServerContext);
  const {
    DynamicPermissions,
    Permissions,
  } = serverContext;

  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);
  const isLinkedInTargetingEnabled = permissions && permissions.dynamic && permissions.dynamic.AudienceSignalTargetSupportForPmax;

  const [isPanelExpanded, setPanelExpanded] = useState(false);

  const togglePanelExpanded = useCallback(() => {
    setPanelExpanded(!isPanelExpanded);
  }, [isPanelExpanded]);

  useEffect(() => {
    if (isEmpty(ageRanges) || isEmpty(genders)) {
      setPanelExpanded(true);
    }
  }, [ageRanges, genders]);

  return (
    <Panel
      className={classes.demographicsContainer}
      expanded={isPanelExpanded}
    >
      <Panel.Heading>
        <Panel.Title toggle onClick={togglePanelExpanded}>
          {i18n.getString(_TL_('Demographics'))}
        </Panel.Title>
      </Panel.Heading>
      <Panel.Collapse>
        <Panel.Body>
          <Alert
            level="Info"
            dismissible={false}
            icon={(<FontIcon iconName="Lightbulb" />)}
            message={i18n.getString(_TL_('Target by age and gender. We recommend you set your demographics broadly to reach more potential customers.'))}
          />
          <Demographic
            className="age-ranges-section"
            i18n={i18n}
            title={i18n.getString(_TL_('Age'))}
            formatter={formatAgeRange}
            dimensions={ageRanges}
            availableDimensions={AgeRanges}
            validationProps={{ field: 'AgeRanges' }}
            popupId="pop_BA_PMAX_Targeting_Age"
          />
          <Demographic
            className="genders-section"
            i18n={i18n}
            title={i18n.getString(_TL_('Gender'))}
            formatter={formatGender}
            dimensions={genders}
            availableDimensions={Genders}
            validationProps={{ field: 'GenderTypes' }}
            popupId="pop_BA_PMAX_Targeting_Gender"
          />
          {
            isLinkedInTargetingEnabled &&
            <React.Fragment>
              <DemographicPicker
                className="company-section"
                i18n={i18n}
                title={i18n.getString(_TL_('Company'))}
                popupId="pop_BA_CampaignCreate_AudTargets_Company"
                profiles={companyName}
                type={ProfileType.CompanyName}
              />
              <DemographicPicker
                className="industry-section"
                i18n={i18n}
                title={i18n.getString(_TL_('Industry'))}
                popupId="pop_BA_CampaignCreate_AudTargets_Industry"
                profiles={industry}
                type={ProfileType.Industry}
              />
              <DemographicPicker
                className="jobfunction-section"
                i18n={i18n}
                title={i18n.getString(_TL_('Job function'))}
                popupId="pop_BA_CampaignCreate_AudTargets_JobFunction"
                profiles={jobFunction}
                type={ProfileType.JobFunction}
              />
            </React.Fragment>
          }
        </Panel.Body>
      </Panel.Collapse>
    </Panel >
  );
};

UnstyledDemographicsSection.propTypes = {
  classes: PropTypes.shape({
    demographicsContainer: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  ageRanges: PropTypes.arrayOf(PropTypes.string).isRequired,
  genders: PropTypes.arrayOf(PropTypes.string).isRequired,
  companyName: PropTypes.arrayOf(PropTypes.shape([])).isRequired,
  industry: PropTypes.arrayOf(PropTypes.shape([])).isRequired,
  jobFunction: PropTypes.arrayOf(PropTypes.shape([])).isRequired,
};

export const DemographicsSection = withDefaultStyles(UnstyledDemographicsSection, getDemographicsSectionStyle);
